import { useEffect, useState, useCallback } from 'react';
import { Card, Table, Button, Badge, Form, Row, Col, Spinner, Alert, Modal } from 'react-bootstrap';
import { api } from '../api/client';
import { formatDateTime } from '../shared/dateFormat';

const STATUSES = ['all', 'demo_scheduled', 'demo_completed', 'hired', 'rejected', 'cancelled'];
const BADGE = { demo_scheduled: 'warning', demo_completed: 'info', hired: 'success', rejected: 'secondary', cancelled: 'secondary' };

export default function AdminManageBookings() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await api.get('/admin/bookings?status=' + status)); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">All Bookings</h3>
        <Button onClick={() => setShowAdd(true)}>+ Add Booking</Button>
      </div>
      {err && <Alert variant="danger" dismissible onClose={() => setErr('')}>{err}</Alert>}
      <Card className="shadow-sm mb-3"><Card.Body>
        <Row><Col md={3}><Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </Form.Select></Col></Row>
      </Card.Body></Card>
      {loading ? <div className="text-center py-4"><Spinner /></div> : (
        <Table bordered hover responsive className="bg-white">
          <thead className="table-dark"><tr><th>#</th><th>Student</th><th>Teacher</th><th>Slot</th><th>Amount held</th><th>Status</th><th>Created</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center text-muted">No bookings</td></tr>}
            {rows.map((b) => (
              <tr key={b.id}>
                <td>{b.id}</td><td>{b.student_name}</td><td>{b.teacher_name}</td>
                <td>{formatDateTime(b.demo_slot_at)}</td><td>₹{Number(b.amount_held).toFixed(0)}</td>
                <td><Badge bg={BADGE[b.status] || 'secondary'}>{b.status.replace('_', ' ')}</Badge></td>
                <td>{formatDateTime(b.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {showAdd && <AddBookingModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}
    </>
  );
}

export function AddBookingModal({ onClose, onDone }) {
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [slots, setSlots] = useState(null);
  const [chosen, setChosen] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([api.get('/admin/students'), api.get('/admin/teachers?status=approved')])
      .then(([s, t]) => { setStudents(s); setTeachers(t); })
      .catch((e) => setErr(e.message));
  }, []);

  useEffect(() => {
    setSlots(null); setChosen(''); setSelectedDate('');
    if (!teacherId) return;
    api.get(`/admin/teachers/${teacherId}/slots`).then(setSlots).catch((e) => setErr(e.message));
  }, [teacherId]);

  const chosenStudent = students.find((s) => String(s.id) === String(studentId));
  const chosenTeacher = teachers.find((t) => String(t.id) === String(teacherId));
  const hold = chosenTeacher ? Number(chosenTeacher.monthly_fee) + Number(chosenTeacher.demo_fee) : 0;
  const wallet = chosenStudent ? Number(chosenStudent.wallet_balance) : 0;
  const enough = wallet >= hold;

  const grouped = (slots || []).reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});
  const dates = Object.keys(grouped);
  useEffect(() => { if (dates.length && !selectedDate) setSelectedDate(dates[0]); }, [dates, selectedDate]);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await api.post('/admin/bookings', {
        student_id: Number(studentId),
        teacher_id: Number(teacherId),
        demo_slot_at: chosen,
      });
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton><Modal.Title>Add booking (demo)</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Student</Form.Label>
          <Form.Select value={studentId} onChange={(e) => setStudentId(e.target.value)}>
            <option value="">Pick a student...</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.email}) — wallet ₹{Number(s.wallet_balance).toFixed(0)}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Teacher</Form.Label>
          <Form.Select value={teacherId} onChange={(e) => setTeacherId(e.target.value)}>
            <option value="">Pick a teacher...</option>
            {teachers.map((t) => <option key={t.id} value={t.id}>{t.name} — ₹{Number(t.monthly_fee).toFixed(0)}/mo + ₹{Number(t.demo_fee).toFixed(0)} demo</option>)}
          </Form.Select>
        </Form.Group>

        {teacherId && (
          <>
            {slots === null ? <div className="text-center py-3"><Spinner size="sm" /> Loading slots...</div> :
             slots.length === 0 ? <Alert variant="warning">This teacher has no open slots in the next 14 days. Ask them to update availability first.</Alert> : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Select value={selectedDate} onChange={(e) => { setSelectedDate(e.target.value); setChosen(''); }}>
                    {dates.map((d) => <option key={d} value={d}>{grouped[d][0].day_label} ({grouped[d].length} open)</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Time</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {(grouped[selectedDate] || []).map((s) => (
                      <Button key={s.datetime} size="sm" variant={chosen === s.datetime ? 'primary' : 'outline-primary'} onClick={() => setChosen(s.datetime)}>
                        {s.start_time} – {s.end_time}
                      </Button>
                    ))}
                  </div>
                </Form.Group>
              </>
            )}
          </>
        )}

        {chosenStudent && chosenTeacher && (
          <Alert variant={enough ? 'info' : 'warning'} className="small py-2 mb-0">
            Will hold <strong>₹{hold.toFixed(0)}</strong> from student's wallet (balance ₹{wallet.toFixed(0)}).
            {!enough && <span> Insufficient — top up first or lower teacher fees.</span>}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={busy || !studentId || !teacherId || !chosen || !enough}>{busy ? 'Booking...' : `Confirm — hold ₹${hold.toFixed(0)}`}</Button>
      </Modal.Footer>
    </Modal>
  );
}
